import { useCallback, useState } from 'react';

/* ============================================================================
 * Feature 2 (frontend) — useImagePaste
 *
 * Attach to the ticket Description <textarea>: when an image is pasted, it
 * uploads via POST /api/kb/upload-image (already open to all roles, returns
 * { url, blobName }) and inserts `![pasted image](url)` at the caret.
 *
 * Drop this file at  src/hooks/useImagePaste.ts
 *
 * The actual UPLOAD is injected (`uploadImage`) so this hook stays decoupled
 * from your auth/transport. Wire it to your useApi()/MSAL flow — see
 * INTEGRATION.md for the exact one-liner (it needs your API base URL + token,
 * which is why a bare relative fetch won't work cross-origin).
 *
 * IMPORTANT: this inserts MARKDOWN. The pasted image only *displays* if the
 * ticket Description is rendered through a markdown renderer. If Description is
 * shown as plain text today, the image won't render — see INTEGRATION.md.
 * ========================================================================== */

/** Uploads one image file and resolves to its public URL. */
export type ImageUploader = (file: File) => Promise<string>;

interface Opts {
  /** Current description text. */
  value: string;
  /** Setter for the description text. */
  onChange: (next: string) => void;
  /** Uploads the pasted blob and returns its URL. */
  uploadImage: ImageUploader;
  /** Optional ref to the <textarea>, so inserts land at the caret (recommended). */
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
}

export function useImagePaste({ value, onChange, uploadImage, textareaRef }: Opts) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const insertAtCaret = useCallback((text: string) => {
    const el = textareaRef?.current;
    if (el && typeof el.selectionStart === 'number') {
      const cur = el.value; // live value — avoids a stale-closure clobber if the user typed during upload
      const start = el.selectionStart;
      const end = el.selectionEnd;
      onChange(cur.slice(0, start) + text + cur.slice(end));
      // restore caret after React re-renders the controlled value
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + text.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      onChange(value ? `${value}\n${text}` : text);
    }
  }, [value, onChange, textareaRef]);

  const onPaste = useCallback(async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData?.items ?? []);
    const images = items.filter(i => i.kind === 'file' && /^image\//.test(i.type));
    if (images.length === 0) return; // not an image paste — let normal text paste through

    e.preventDefault();
    setUploading(true);
    setError(null);
    try {
      const urls: string[] = [];
      for (const item of images) {
        const file = item.getAsFile();
        if (file) urls.push(await uploadImage(file));
      }
      if (urls.length) {
        insertAtCaret(urls.map(u => `![pasted image](${u})`).join('\n'));
      }
    } catch (err: any) {
      setError(err?.message ?? 'Image upload failed');
    } finally {
      setUploading(false);
    }
  }, [insertAtCaret, uploadImage]);

  return { onPaste, uploading, error };
}

/* ----------------------------------------------------------------------------
 * Reference uploader. Prefer wiring to useApi() (see INTEGRATION.md). This
 * fallback needs BOTH the API base URL and a token getter, because the SPA and
 * API are on different origins (helpdesk.societybrands.com vs
 * app-helpdesk-pro-api.azurewebsites.net) so a relative '/api/...' won't reach
 * the backend.
 * -------------------------------------------------------------------------- */
export function makeFetchUploader(
  apiBaseUrl: string,
  getAccessToken: () => Promise<string>,
): ImageUploader {
  return async (file: File) => {
    const fd = new FormData();
    fd.append('image', file); // field name MUST be "image" (multer.single('image'))
    const token = await getAccessToken();
    const res = await fetch(`${apiBaseUrl.replace(/\/$/, '')}/api/kb/upload-image`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }, // do NOT set Content-Type; the browser sets the multipart boundary
      body: fd,
    });
    if (!res.ok) throw new Error(`Upload failed (${res.status})`);
    const data = await res.json();
    // Return a stable reference (not the raw blob URL): the container is private, so the
    // <img> can't load the bare URL. DescriptionBody resolves `kbimg:<blob>` to a fresh
    // short-lived SAS at display time. Fall back to the raw URL only if blobName is absent.
    return (data.blobName ? `kbimg:${data.blobName}` : data.url) as string;
  };
}
